import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Checkbox,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";
import { useMemo, useState } from "react";
import { QuizRounded } from "@mui/icons-material";
import PageHeader from "../components/page-header";
import { Dialogs } from "../components/dialogs";
import StickyHeadTable, { Column } from "../components/table";
import { Pergunta, Resposta } from "../types/general";
import { ViewModelPergunta } from "../viewmodel/viewmodel-pergunta";

function PerguntasPage() {
  const viewModel = ViewModelPergunta();
  const [open, setOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const { viewMode, nivel: nivelFiltro } = viewModel;

  // Form state
  const [questao, setQuestao] = useState("");
  const [respostas, setRespostas] = useState<Resposta[]>([
    { res: "", status: false },
    { res: "", status: false },
    { res: "", status: false },
    { res: "", status: false },
  ]);
  const [nivel, setNivel] = useState(1);
  const [temaId, setTemaId] = useState<number>(0);

  const handleOpen = () => {
    setEditMode(false);
    setQuestao("");
    setRespostas([
      { res: "", status: false },
      { res: "", status: false },
      { res: "", status: false },
      { res: "", status: false },
    ]);
    setNivel(1);
    if (viewModel.temaIdParam) {
      setTemaId(Number(viewModel.temaIdParam));
    }
    setOpen(true);
  };

  const handleEdit = (id: number) => {
    const pergunta = viewModel.perguntas.data.find((p) => p.id === id);
    if (pergunta) {
      setEditMode(true);
      setQuestao(pergunta.questao);
      setRespostas(pergunta.respostas);
      setNivel(pergunta.nivel);
      setTemaId(pergunta.tema_id || 0);
      viewModel.selectPergunta(pergunta);
      setOpen(true);
    }
  };

  const handleRespostaChange = (
    index: number,
    field: keyof Resposta,
    value: string | boolean
  ) => {
    const newRespostas = [...respostas];
    newRespostas[index] = { ...newRespostas[index], [field]: value };
    setRespostas(newRespostas);
  };

  const validateForm = (): string | null => {
    if (!questao.trim()) {
      return "A questão é obrigatória";
    }
    if (!temaId) {
      return "Selecione um tema";
    }
    for (let i = 0; i < respostas.length; i++) {
      if (!respostas[i].res.trim()) {
        return `A resposta ${i + 1} é obrigatória`;
      }
    }
    const correctAnswers = respostas.filter((r) => r.status === true);
    if (correctAnswers.length !== 1) {
      return "Deve haver exatamente uma resposta correta";
    }
    return null;
  };

  const handleSubmit = async () => {
    const error = validateForm();
    if (error) {
      viewModel.showWarning({
        title: "Erro",
        message: error,
      });
      return;
    }

    setOpen(false);
    const perguntaData = {
      questao,
      respostas,
      nivel,
      tema_id: temaId,
      visible: editMode && viewModel.selectedPergunta?.visible,
      author: editMode ? viewModel.selectedPergunta?.author : undefined,
    };

    editMode && viewModel.selectedPergunta?.id
      ? viewModel.handleActionPergunta(
        { id: viewModel.selectedPergunta.id, ...perguntaData },
        "update"
      )
      : viewModel.handleActionPergunta(perguntaData, "add");
  };

  const handleDelete = (id: number) => {
    const pergunta = viewModel.perguntas.data.find((p) => p.id === id);
    viewModel.showConfirmation({
      title: "Confirmar Eliminação",
      message: `Tem a certeza que deseja eliminar a pergunta "${pergunta?.questao}"? Esta ação não pode ser desfeita.`,
      onConfirm: () => viewModel.handleDeletePergunta(id),
    });
  };

  const handleValidate = (id: number) => {
    const pergunta = viewModel.perguntas.data.find((p) => p.id === id);
    viewModel.showConfirmation({
      title: "Confirmar Validação",
      message: `Tem a certeza que deseja validar a pergunta "${pergunta?.questao}"?`,
      onConfirm: () => viewModel.handleValidatePergunta(id),
    });
  };

  const handleInvalidate = (id: number) => {
    const pergunta = viewModel.perguntas.data.find((p) => p.id === id);
    viewModel.showConfirmation({
      title: "Confirmar Invalidação",
      message: `Tem a certeza que deseja invalidar a pergunta "${pergunta?.questao}"?`,
      onConfirm: () => viewModel.handleRejectPergunta(id),
    });
  };

  const getNivelLabel = (nivel: number) => {
    switch (nivel) {
      case 1:
        return "Fácil";
      case 2:
        return "Médio";
      case 3:
        return "Difícil";
      default:
        return "Desconhecido";
    }
  };

  const getNivelColor = (nivel: number): "success" | "warning" | "error" | "info" => {
    switch (nivel) {
      case 1:
        return "success";
      case 2:
        return "warning";
      case 3:
        return "error";
      default:
        return "info";
    }
  };

  const columns: Column<Pergunta>[] = [
    { id: "id", label: "ID", minWidth: 80 },
    { id: "questao", label: "Questão", minWidth: 500 },
    {
      id: "nivel",
      label: "Nível",
      minWidth: 80,
      renderCell: (row) => (
        <Chip
          label={getNivelLabel(row.nivel)}
          color={getNivelColor(row.nivel)}
          size="small"
        />
      ),
    },
    {
      id: "tema_nome",
      label: "Tema",
      minWidth: 150,
    },
    {
      id: "visible",
      label: "Validado",
      minWidth: 80,
      renderCell: (row) => (
        <Chip
          label={row.visible ? "Sim" : "Não"}
          color={row.visible ? "success" : "default"}
          size="small"
        />
      ),
    },
    {
      id: "actions",
      label: "Ações",
      minWidth: 150,
      renderCell: (row) => (
        <Box>
          <Tooltip title="Editar" arrow>
            <IconButton
              size="small"
              onClick={() => handleEdit(row.id!)}
              color="primary"
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {row.visible ? (
            <Tooltip title="Invalidar" arrow>
              <IconButton
                size="small"
                color="warning"
                onClick={() => handleInvalidate(row.id!)}
              >
                <CancelIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip title="Validar" arrow>
              <IconButton
                size="small"
                color="success"
                onClick={() => handleValidate(row.id!)}
              >
                <CheckCircleIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Eliminar" arrow>
            <IconButton
              size="small"
              color="error"
              onClick={() => handleDelete(row.id!)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const data = useMemo(() => {
    return viewModel.perguntas.data.map((item) => {
      return {
        ...item,
        tema_nome: viewModel.temas.data.find((tema) => item.tema_id === tema.id)?.nome
      }
    })
  }, [viewModel.temas, viewModel.perguntas])

  // Calculate statistics - use tema data when available, otherwise calculate from data
  const stats = useMemo(() => {
    if (viewModel.tema) {
      // Use data from /tema/:id endpoint
      const temaStats = viewModel.tema;
      return {
        total: (Number(temaStats?.easy) ?? 0) + (Number(temaStats.medium) ?? 0) + (Number(temaStats.dificult) ?? 0),
        easy: temaStats.easy,
        medium: temaStats.medium,
        hard: temaStats.dificult
      };
    } else {
      // Fallback to calculating from data
      const total = data.length;
      const easy = data.filter((p) => p.nivel === 1).length;
      const medium = data.filter((p) => p.nivel === 2).length;
      const hard = data.filter((p) => p.nivel === 3).length;
      return { total, easy, medium, hard };
    }
  }, [data, viewModel.tema]);

  const nivelFilters: {
    label: string;
    title: string;
    value: number | undefined;
    color: "primary" | "success" | "warning" | "error";
    count: number | string;
  }[] = [
    { label: "Todos", title: "Total de Perguntas", value: undefined, color: "primary", count: stats.total },
    { label: "Fáceis", title: "Fáceis", value: 1, color: "success", count: stats.easy ?? 0 },
    { label: "Médios", title: "Médias", value: 2, color: "warning", count: stats.medium ?? 0 },
    { label: "Difíceis", title: "Difíceis", value: 3, color: "error", count: stats.hard ?? 0 },
  ];

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <PageHeader
        icon={<QuizRounded />}
        color="#EC4899"
        title="Gestão de Perguntas"
        subtitle={viewModel.selectedTemaId ? `Filtrado por tema ID: ${viewModel.selectedTemaId}` : undefined}
        onBack={viewModel.temaIdParam ? viewModel.goToTemas : () => viewModel.navigate("/painel")}
        actions={
          <>
            <Button
              variant={viewMode === "all" ? "contained" : "outlined"}
              onClick={() => viewModel.changeViewMode("all")}
            >
              Todas
            </Button>
            <Button
              variant={viewMode === "suggestions" ? "contained" : "outlined"}
              startIcon={<LightbulbIcon />}
              onClick={() => viewModel.changeViewMode("suggestions")}
            >
              Sugestões
            </Button>
            <Button variant="contained" onClick={handleOpen}>
              Nova Pergunta
            </Button>
          </>
        }
      />

      {/* Cards com contagem por nível: também funcionam como filtro */}
      {viewModel.temaIdParam && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          {nivelFilters.map((f) => {
            const active = nivelFiltro === f.value;
            return (
              <Grid item xs={12} sm={6} md={3} key={f.label}>
                <Card
                  sx={{
                    backgroundColor: `${f.color}.main`,
                    color: "white",
                    transition: "transform 0.2s, box-shadow 0.2s",
                    outline: active ? "3px solid" : "3px solid transparent",
                    outlineColor: active ? `${f.color}.dark` : "transparent",
                    outlineOffset: 2,
                    "&:hover": { transform: "translateY(-5px)", boxShadow: 6 },
                  }}
                >
                  <CardActionArea
                    onClick={() => viewModel.changeNivel(f.value)}
                    aria-pressed={active}
                  >
                    <CardContent sx={{ textAlign: "center" }}>
                      <Typography variant="h4" component="div">
                        {f.count}
                      </Typography>
                      <Typography variant="body2">{f.title}</Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Sem tema não há cards: filtro por nível em chips (sem contagem) */}
      {!viewModel.temaIdParam && (
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1, mb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mr: 0.5, fontWeight: 600 }}>
            Nível:
          </Typography>
          {nivelFilters.map((f) => {
            const active = nivelFiltro === f.value;
            return (
              <Chip
                key={f.label}
                clickable
                label={f.label}
                color={active ? f.color : "default"}
                variant={active ? "filled" : "outlined"}
                onClick={() => viewModel.changeNivel(f.value)}
              />
            );
          })}
        </Box>
      )}

      <StickyHeadTable
        columns={columns}
        rows={data}
        getRowId={(row) => row.id!}
        pagination={{
          totalData: viewModel.perguntas.pagination.totalData,
          totalPages: viewModel.perguntas.pagination.totalPages,
          currentPage: viewModel.perguntas.pagination.currentPage,
          onPageChange: (newPage) => viewModel.reload({ page: newPage }),
          onRowsPerPageChange: () => { },
        }}
      />

      {/* Create/Edit Dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editMode ? "Editar Pergunta" : "Nova Pergunta"}</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="normal">
            <InputLabel>Tema</InputLabel>
            <Select
              value={temaId}
              label="Tema"
              onChange={(e) => setTemaId(Number(e.target.value))}
              disabled={!editMode && !!viewModel.temaIdParam}
            >
              {viewModel.temas.data.map((tema) => (
                <MenuItem key={tema.id} value={tema.id}>
                  {tema.nome}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth margin="normal">
            <InputLabel>Nível</InputLabel>
            <Select
              value={nivel}
              label="Nível"
              onChange={(e) => setNivel(Number(e.target.value))}
            >
              <MenuItem value={1}>Fácil</MenuItem>
              <MenuItem value={2}>Médio</MenuItem>
              <MenuItem value={3}>Difícil</MenuItem>
            </Select>
          </FormControl>

          <TextField
            autoFocus
            margin="normal"
            label="Questão"
            fullWidth
            multiline
            rows={3}
            value={questao}
            onChange={(e) => setQuestao(e.target.value)}
          />

          <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
            Respostas (selecione a correta)
          </Typography>

          {respostas.map((resposta, index) => (
            <Box key={index} sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <TextField
                fullWidth
                label={`Resposta ${index + 1}`}
                value={resposta.res}
                onChange={(e) => handleRespostaChange(index, "res", e.target.value)}
                sx={{ mr: 1 }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={resposta.status}
                    onChange={(e) => handleRespostaChange(index, "status", e.target.checked)}
                  />
                }
                label="Correta"
              />
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancelar</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editMode ? "Atualizar" : "Criar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialogs {...viewModel.dialog} />
    </Container>
  );
}

export default PerguntasPage;
